import React, { useCallback, useState } from 'react';
import MainFrameLeftSection from '../../components/MainFrameLeftSection/mainFrameLeftSection';
// eslint-disable-next-line import/no-cycle
import MainFrameRightAttachingSection from '../../components/MainFrameRightAttachingSection/mainFrameRightAttachingSection';
// eslint-disable-next-line import/no-cycle
import {
  DocumentsAndSearchType, Pagination, SearchType, defaultDocumentsAndSearchType, defaultPagination,
} from '../../components/MainFrameRightSection/mainFrameRightSection';
import styles from '../mainPage/mainPage.module.css';
import { Folder } from '../../services/http/folder.api';
import FolderPermissionsSection from '../../components/FolderPermissions/folderPermissionsSection';
import { useDocumentSearchDetail, useDocumentSearchFolder } from '../../hooks/api/document.hook';
import { useMessageModal } from '../../hooks/modal.hook';
import { ApiError } from '../../services/http';
import {
  Document,
  DocumentDetail, DocumentPagination, DocumentSearchDetailForm, SortList,
} from '../../services/http/documents.api';
import LoadingOverlay from '../../components/LoadingOverlay';
import TopBar from '../../components/TopBar/topBar';
import SearchInputTop, {
  ContractType,
  FilterType, mapContractTypeToFlags,
  mapWarehouseStatusToCode,
  WarehouseStatusType,
} from '../../components/SearchInputTop';
import MainFrame from '../../components/MainFrame/mainFrame';
import TopBarTagBulkButton from '../../components/TopBar/topBarTagBulkButton';
import useColumnResize from '../../hooks/useColumnResize';
import { LastSearch } from '../../components/SearchInputTop/SearchInputTopFilterModal';
import { WarehouseStatusCode } from '../../utils/warehouse.utils';

/**
 * 選択された関連ドキュメントのプロップスインターフェース
 */
export interface ChooseRelatedDocumentProps {
  /** 関連文書が選択されたときに呼び出される関数 */
  onChooseRelatedDocuments: (documents: Document[]) => void,
  /** 関連文書の追加がキャンセルされたときに呼び出される関数 */
  onCancelAddingRelatedDocuments: () => void,
}

/**
 * 選択された関連ドキュメントのコンポーネント
 */
function ChooseRelatedDocument({
  onChooseRelatedDocuments = () => {},
  onCancelAddingRelatedDocuments = () => {},
}: ChooseRelatedDocumentProps) {
  const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);
  const [changingFolderPermissions, setChangingFolderPermissions] = useState(false);
  const [updateFolderList, setUpdateFolderList] = useState(0);
  // ------------------
  const [isSearching, setIsSearching] = useState(false);
  const [sort, setSort] = useState<SortList[] | undefined>(undefined);
  const [pagination, setPagination] = useState<Pagination>(defaultPagination);
  const [documentsAndSearchType, setDocumentsAndSearchType] = useState<DocumentsAndSearchType>(defaultDocumentsAndSearchType);
  const [advancedSearchForm, setAdvancedSearchForm] = useState<DocumentSearchDetailForm | null>(null);
  const [searchText, setSearchText] = useState<string | null>(null);
  const [highlightTexts, setHighlightTexts] = useState<string[]>([]);
  const [contractType, setContractType] = useState<ContractType>(ContractType.ALL);
  const [warehouseStatus, setWarehouseStatus] = useState<WarehouseStatusType>(WarehouseStatusType.ALL);
  const [isSearchingOcr, setIsSearchingOcr] = useState(false);
  const [lastAdvancedSearchForm, setLastAdvancedSearchForm] = useState<LastSearch | null>(null);
  const [simpleSearchForm, setSimpleSearchForm] = useState<DocumentSearchDetailForm | null>(null);

  const { request: documentSearchFolderRequest, loading: loadingFolder } = useDocumentSearchFolder();
  const { request: documentSearchDetailRequest, loading: loadingAdvancedSearch } = useDocumentSearchDetail();
  const openMessageModal = useMessageModal();
  const { width: leftWidth, handleResizeStart } = useColumnResize(
    'chooseRelatedDocumentLeftSectionWidth',
    220,
  );

  const isLoading = loadingFolder || loadingAdvancedSearch;

  const onClearSearch = useCallback(() => {
    setSearchText('');
    setIsSearching(false);
    setIsSearchingOcr(false);
    setLastAdvancedSearchForm(null);
  }, []);

  const onClearAdvancedSearch = useCallback(() => {
    setAdvancedSearchForm(null);
  }, []);

  const searchFolderDocument = useCallback(async (_folder: Folder, page: number, sortList = sort) => {
    try {
      const res = await documentSearchFolderRequest({ folder: _folder, page, sortList: sortList || [] });
      setDocumentsAndSearchType({ documents: res.documents, searchType: SearchType.FOLDER });
      const totalPages = Math.ceil(res.total / res.pageLimit) || 1;
      setPagination({
        page: res.page,
        totalItems: res.total,
        pageLimit: res.pageLimit,
        totalPages,
      });
    } catch (e) {
      openMessageModal((e as ApiError)?.message);
    }
  }, [documentSearchFolderRequest, openMessageModal, sort]);

  const handleSimpleSearchReturn = useCallback((res: DocumentPagination) => {
    setDocumentsAndSearchType({ documents: res.documents, searchType: SearchType.SIMPLE });
    setIsSearching(true);
    setSelectedFolder(null);

    const totalPages = Math.ceil(res.total / res.pageLimit) || 1;
    setPagination({
      page: res.page,
      totalItems: res.total,
      pageLimit: res.pageLimit,
      totalPages,
    });
    setHighlightTexts(res.highlightWordList);
  }, []);

  const executeSimpleSearch = useCallback(async (
    page: number,
    sortList: SortList[] | undefined,
    text: string,
    status: WarehouseStatusCode | undefined,
    electronicContract: 1 | 0 | undefined,
  ) => {
    try {
      const res = await documentSearchDetailRequest(
        {
          page,
          sortList: sortList || [],
          simpleString: text,
          warehouseStatus: status,
          electronicContract,
        },
      );

      handleSimpleSearchReturn(res);
    } catch (e) {
      setIsSearching(false);
      openMessageModal((e as ApiError)?.message);
    }
  }, [documentSearchDetailRequest, handleSimpleSearchReturn, openMessageModal]);

  const prepareSimpleSearchParams = useCallback((
    warehouseStatusParam: WarehouseStatusType | undefined,
    contractTypeParam: ContractType | undefined,
  ) => {
    const status = mapWarehouseStatusToCode(warehouseStatusParam || warehouseStatus);
    const electronicContract = mapContractTypeToFlags(contractTypeParam || contractType);
    return { status, electronicContract };
  }, [contractType, warehouseStatus]);

  const handleSimpleSearchDocument = useCallback(async (
    text: string,
    page: number,
    sortList = sort,
    warehouseStatusParam?: WarehouseStatusType,
    contractTypeParam?: ContractType,
  ) => {
    setLastAdvancedSearchForm(null);
    setAdvancedSearchForm(null);
    setSort(sortList);

    const { status, electronicContract } = prepareSimpleSearchParams(warehouseStatusParam, contractTypeParam);

    setSimpleSearchForm({
      simpleString: text,
      warehouseStatus: status,
      electronicContract,
    });

    await executeSimpleSearch(page, sortList, text, status, electronicContract);
  }, [executeSimpleSearch, prepareSimpleSearchParams, sort]);

  const handleFolderSelect = useCallback((folder: Folder) => {
    if (selectedFolder && (selectedFolder.id === folder.id)) return;
    setSelectedFolder(folder);
    searchFolderDocument(folder, 1);
    if (!isSearching) return;
    setSearchText(null);
    setLastAdvancedSearchForm(null);
  }, [isSearching, searchFolderDocument, selectedFolder]);

  const handleAdvancedSearchResponse = useCallback((res: DocumentPagination) => {
    setDocumentsAndSearchType({ documents: res.documents, searchType: SearchType.ADVANCED });

    setIsSearching(true);
    setSelectedFolder(null);

    const totalPages = Math.ceil(res.total / res.pageLimit) || 1;
    setPagination({
      page: res.page,
      totalItems: res.total,
      pageLimit: res.pageLimit,
      totalPages,
    });

    setHighlightTexts(res.highlightWordList);
  }, []);

  const executeAdvancedSearch = useCallback(async (updatedForm: DocumentSearchDetailForm, page: number, sortList: SortList[] | undefined) => {
    try {
      const res = await documentSearchDetailRequest({ ...updatedForm, page, sortList: sortList || [] });

      handleAdvancedSearchResponse(res);
    } catch (e) {
      openMessageModal((e as ApiError)?.message);
    }
  }, [documentSearchDetailRequest, handleAdvancedSearchResponse, openMessageModal]);

  const applyFiltersFromForm = useCallback((updatedForm: DocumentSearchDetailForm) => {
    const wareHouseStatusFromForm = updatedForm.warehouseStatus;
    const contractTypeFromForm = updatedForm.electronicContract;

    if (wareHouseStatusFromForm === WarehouseStatusCode.IN_STORAGE) {
      setWarehouseStatus(WarehouseStatusType.STORED);
    } else if (wareHouseStatusFromForm === WarehouseStatusCode.NOT_RECEIVED) {
      setWarehouseStatus(WarehouseStatusType.NOT_STORED);
    } else {
      setWarehouseStatus(WarehouseStatusType.ALL);
    }

    if (contractTypeFromForm === 1) {
      setContractType(ContractType.ELECTRONIC);
    } else if (contractTypeFromForm === 0) {
      setContractType(ContractType.PAPER);
    } else {
      setContractType(ContractType.ALL);
    }
  }, []);

  const buildSearchForm = useCallback((
    form: DocumentSearchDetailForm,
    warehouseStatusParam: WarehouseStatusType | undefined,
    contractTypeParam: ContractType | undefined,
  ) => {
    const updatedForm: DocumentSearchDetailForm = { ...form };

    if (warehouseStatusParam) updatedForm.warehouseStatus = mapWarehouseStatusToCode(warehouseStatusParam);
    if (contractTypeParam) updatedForm.electronicContract = mapContractTypeToFlags(contractTypeParam);
    return updatedForm;
  }, []);

  const handleAdvancedSearchDocument = useCallback(async (
    form: DocumentSearchDetailForm,
    page: number,
    sortList = sort,
    warehouseStatusParam?: WarehouseStatusType,
    contractTypeParam?: ContractType,
  ) => {
    setSort(sortList);
    setHighlightTexts([]);

    const updatedForm = buildSearchForm(form, warehouseStatusParam, contractTypeParam);

    applyFiltersFromForm(updatedForm);

    await executeAdvancedSearch(updatedForm, page, sortList);
  }, [sort, buildSearchForm, applyFiltersFromForm, executeAdvancedSearch]);

  const handleAdvancedSearch = useCallback((form: DocumentSearchDetailForm) => {
    setSearchText(null);
    handleAdvancedSearchDocument(form, 1);
    setAdvancedSearchForm(form);
  }, [handleAdvancedSearchDocument]);

  const handlePageChange = useCallback((page: number) => {
    if (documentsAndSearchType.searchType === SearchType.ADVANCED && advancedSearchForm) {
      handleAdvancedSearchDocument(advancedSearchForm, page);
      return;
    }
    if (documentsAndSearchType.searchType === SearchType.SIMPLE && searchText !== null) {
      handleSimpleSearchDocument(searchText, page);
      return;
    }
    if (documentsAndSearchType.searchType === SearchType.FOLDER && selectedFolder) {
      searchFolderDocument(selectedFolder, page);
    }
  }, [handleAdvancedSearchDocument, advancedSearchForm, documentsAndSearchType.searchType, handleSimpleSearchDocument, searchFolderDocument, searchText, selectedFolder]);

  const handleSearch = useCallback((text: string) => {
    handleSimpleSearchDocument(text, 1);
    setSearchText(text);
  }, [handleSimpleSearchDocument]);

  const onRefresh = useCallback((page?: number, sortList?: SortList[]) => {
    if (documentsAndSearchType.searchType === SearchType.ADVANCED && advancedSearchForm) {
      handleAdvancedSearchDocument(advancedSearchForm, page || 1, sortList);
      return;
    }
    if (documentsAndSearchType.searchType === SearchType.SIMPLE && searchText !== null) {
      handleSimpleSearchDocument(searchText, page || 1, sortList);
      return;
    }
    if (documentsAndSearchType.searchType === SearchType.FOLDER && selectedFolder) {
      searchFolderDocument(selectedFolder, page || 1, sortList);
    }
  }, [handleAdvancedSearchDocument, advancedSearchForm, documentsAndSearchType.searchType, handleSimpleSearchDocument, searchFolderDocument, searchText, selectedFolder]);

  const onSortChanged = useCallback((sortList: SortList[]) => {
    const result = sortList.map(({ item, order }) => ({
      item: item === 'name' ? 'documentName' : item,
      order,
    }));

    setSort(result);
    if (pagination.totalPages !== 1) {
      onRefresh(1, result);
    }
  }, [onRefresh, pagination.totalPages]);

  const onRemoveDocument = useCallback((e: DocumentDetail) => {
    setDocumentsAndSearchType((prev) => ({
      ...prev,
      documents: prev.documents.filter((document) => document.id !== e.id),
    }));
  }, []);

  const handleSearchDocument = useCallback((filterParam: WarehouseStatusType | ContractType, filterType: FilterType) => {
    const isContractFilter = filterType === FilterType.CONTRACT_TYPE;
    const isWarehouseFilter = filterType === FilterType.WAREHOUSE_STATUS_TYPE;
    if (!isContractFilter && !isWarehouseFilter) return;

    const contractTypeParam = isContractFilter ? (filterParam as ContractType) : contractType;
    const warehouseStatusParam = isWarehouseFilter ? (filterParam as WarehouseStatusType) : warehouseStatus;

    if (advancedSearchForm) {
      setSearchText(null);
      handleAdvancedSearchDocument(advancedSearchForm, 1, undefined, warehouseStatusParam, contractTypeParam);
    } else {
      const searchString = simpleSearchForm?.simpleString || '';
      setSearchText(searchString);
      handleSimpleSearchDocument(searchString, 1, undefined, warehouseStatusParam, contractTypeParam);
    }
  }, [advancedSearchForm, contractType, handleAdvancedSearchDocument, handleSimpleSearchDocument, simpleSearchForm, warehouseStatus]);

  return (
    <div className={styles.attachDocSection}>
      <MainFrame
        borderBox
        topBarDisplay={false}
        onClearSearch={onClearSearch}
        onClearAdvancedSearch={onClearAdvancedSearch}
      >
        <div className={styles.mainFrame}>
          <TopBar>
            <SearchInputTop
              text={searchText || ''}
              isSearching={isSearching}
              contractType={contractType}
              warehouseStatus={warehouseStatus}
              onAdvancedSearch={handleAdvancedSearch}
              onSearch={handleSearch}
              onTextChange={setSearchText}
              onChangeContractType={(status, filterType) => {
                setContractType(status);
                handleSearchDocument(status, filterType);
              }}
              onChangeWarehouseStatus={(status, filterType) => {
                setWarehouseStatus(status);
                handleSearchDocument(status, filterType);
              }}
              setLastAdvancedSearchForm={setLastAdvancedSearchForm}
              lastAdvancedSearchForm={lastAdvancedSearchForm}
            />
            <TopBarTagBulkButton />
          </TopBar>
          <div className={styles.mainSections}>
            {!isSearching ? (
              <MainFrameLeftSection
                onDocumentFolderChanged={() => onRefresh(pagination.page, sort)}
                folder={selectedFolder}
                onSelectFolder={handleFolderSelect}
                isChangingFolderPermissions={changingFolderPermissions}
                updateFolderList={updateFolderList}
                mainFrameLeftSectionWidth={leftWidth}
              />
            ) : null}
            <div
              role="separator"
              style={{
                width: '5px',
                cursor: 'col-resize',
                backgroundColor: 'transparent',
                margin: '-3px',
                zIndex: 999,
                height: '100%',
                maxHeight: '100%',
              }}
              onMouseDown={handleResizeStart}
            />
            {!changingFolderPermissions && (
            <MainFrameRightAttachingSection
              onChooseRelatedDocuments={onChooseRelatedDocuments}
              onCancelAddingRelatedDocuments={onCancelAddingRelatedDocuments}
              folder={selectedFolder}
              documentsAndSearchType={documentsAndSearchType}
              pagination={pagination}
              isSearching={isSearching}
              highlightTexts={highlightTexts}
              onPageChanged={handlePageChange}
              onSortChanged={onSortChanged}
              onRemoveDocument={onRemoveDocument}
              onRefresh={onRefresh}
              isSearchingOcr={isSearchingOcr}
            />
            )}
            {changingFolderPermissions && (
            <FolderPermissionsSection
              mainFrameLeftSectionWidth={leftWidth}
              folder={selectedFolder}
              onCancel={() => setChangingFolderPermissions(false)}
              updateFolderList={() => setUpdateFolderList((prev) => prev + 1)}
            />
            )}
          </div>
        </div>
      </MainFrame>
      {isLoading ? <LoadingOverlay /> : null}
    </div>
  );
}

export default ChooseRelatedDocument;
